import React, { useEffect, useLayoutEffect, useState } from "react";
import { DropDownComponent } from "../../dropdown/DropDownComponent";

const FILTER_FIELDS = [
  { value: "userType", label: "User Type" },
  { value: "companyName", label: "Company Name" },
  { value: "userRole", label: "User Role" },
  { value: "status", label: "Status" },
];

const FIELD_ORDER = ["userType", "companyName", "userRole", "status"];
const FIELDS_REQUIRING_USER_TYPE = ["companyName", "userRole"];

const filterSelectStyles = {
  control: (base) => ({
    ...base,
    alignItems: "center",
    display: "flex",
    minHeight: 32,
    border: "1px solid #c9d8e6",
    borderRadius: 7,
    backgroundColor: "#ffffff",
    boxShadow: "none",
    fontSize: 13,
  }),
  valueContainer: (base) => ({
    ...base,
    flexWrap: "wrap",
    padding: "4px 8px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 100000 }),
  menu: (base) => ({
    ...base,
    zIndex: 100000,
    border: "1px solid #c9d8e6",
    boxShadow: "0 12px 26px rgba(36, 73, 104, 0.18)",
  }),
};

const UserMasterFilter = (props) => {
  const {
    userTypedrp,
    handleUserFilterChange,
    userType,
    DistributorData,
    CompanyData,
    handleCompanyFilterChange,
    companyName,
    userRoleData,
    handleRoleFilterChange,
    UserRole,
    statusdrp,
    handleStatusFilterChange,
    ClearFilter,
    ApplyFilter,
    status,
    onCloseFilter,
    positioningTargetRef,
    activeFields,
    setActiveFields,
  } = props;

  const [menuPosition, setMenuPosition] = useState(null);
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  const isDistributorType = userType?.value === "2";
  const isEmployeeType = userType?.value === "1";
  const isUserTypeChosen = Boolean(userType?.value);

  // "Filter by" options available right now — Company Name / User Role are
  // hidden until a User Type value is chosen.
  const availableFilterFields = isUserTypeChosen
    ? FILTER_FIELDS
    : FILTER_FIELDS.filter((f) => !FIELDS_REQUIRING_USER_TYPE.includes(f.value));

  const companyDropdownConfig = isDistributorType
    ? { dpDownName: "distributor", stateData: DistributorData }
    : { dpDownName: "company", stateData: CompanyData };

  const closeFilterDropdown = (event) => {
    event.stopPropagation();
    onCloseFilter?.();
  };

  useLayoutEffect(() => {
    if (!positioningTargetRef?.current || typeof window === "undefined") return undefined;

    const updateMenuPosition = () => {
      const targetRect = positioningTargetRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const rightEdge = Math.min(viewportWidth - 12, targetRect.right);
      const maxWidth = Math.min(800, viewportWidth - 24);
      const preferredWidth = Math.min(maxWidth, Math.max(560, targetRect.width));
      const availableWidth = Math.max(320, rightEdge - 12);
      const width = Math.min(preferredWidth, availableWidth);
      const top = Math.min(targetRect.bottom + 8, viewportHeight - 96);

      setMenuPosition({
        "--quote-filter-top": `${Math.max(8, top)}px`,
        "--quote-filter-left": `${Math.max(12, rightEdge - width)}px`,
        "--quote-filter-width": `${width}px`,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [positioningTargetRef]);

  const clearFieldValue = (field) => {
    if (field === "userType") handleUserFilterChange(null);
    if (field === "companyName") handleCompanyFilterChange(null);
    if (field === "userRole") handleRoleFilterChange(null);
    if (field === "status") handleStatusFilterChange(null);
  };

  useEffect(() => {
    if (isUserTypeChosen) return;

    const hasRestrictedRow = activeFields.some((f) => FIELDS_REQUIRING_USER_TYPE.includes(f));
    if (!hasRestrictedRow) return;

    FIELDS_REQUIRING_USER_TYPE.forEach((f) => {
      if (activeFields.includes(f)) clearFieldValue(f);
    });

    setActiveFields((prev) => {
      const remaining = prev.filter((f) => !FIELDS_REQUIRING_USER_TYPE.includes(f));
      return remaining.length > 0 ? remaining : ["userType"];
    });
  }, [isUserTypeChosen]);

  const addFilterRow = () => {
    const unusedField = availableFilterFields
      .map((f) => f.value)
      .find((value) => !activeFields.includes(value));
    if (unusedField) setActiveFields((prev) => [...prev, unusedField]);
  };

  const removeFilterRow = (rowIndex) => {
    if (activeFields.length <= 1) return;
    const field = activeFields[rowIndex];
    clearFieldValue(field);
    setActiveFields((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const changeRowField = (rowIndex, newField) => {
    setActiveFields((prev) => {
      const oldField = prev[rowIndex];
      if (oldField === newField) return prev;
      clearFieldValue(oldField);
      const next = [...prev];
      next[rowIndex] = newField;
      return next;
    });
  };

  const handleClearAll = (event) => {
    ClearFilter();
    setActiveFields(["userType"]);
    closeFilterDropdown(event);
  };

  const orderedFields = FIELD_ORDER
    .filter((field) => activeFields.includes(field))
    .map((field) => ({ field, index: activeFields.indexOf(field) }));

  return (
    <div
      className={`dropdown-menu filter-dropdown quote-filter-menu p-0 show ${menuPosition ? "quote-filter-menu-fixed" : ""}`}
      aria-labelledby="filterDropdown"
      onClick={(event) => event.stopPropagation()}
      style={menuPosition || undefined}
    >
      <div className="quote-filter-panel user-master-filter-panel">
        <div className="quote-filter-header">
          <div>
            <h6>
              <i className="fas fa-filter mr-2"></i>
              Filters
            </h6>
            <p>Build focused user list conditions</p>
          </div>
          <button
            className="btn filter-close-btn"
            type="button"
            onClick={closeFilterDropdown}
            title="Close filters"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="quote-filter-builder">
          {orderedFields.map(({ field, index }) => {
            const usedElsewhere = activeFields.filter((_, i) => i !== index);
            const companyLabel = isDistributorType ? "Distributor Name" : "Company Name";

            const rowDropdownOptions = FILTER_FIELDS.filter(
              (f) => availableFilterFields.some((af) => af.value === f.value) || f.value === field
            );

            return (
              <div className="quote-filter-row align-items-center" key={field}>
                <div className="filter-field-cell">
                  <label>Filter by</label>
                  <select
                    className="form-control"
                    value={field}
                    onChange={(event) => changeRowField(index, event.target.value)}
                  >
                    {rowDropdownOptions.map((f) => (
                      <option key={f.value} value={f.value} disabled={usedElsewhere.includes(f.value)}>
                        {f.value === "companyName" ? companyLabel : f.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-value-cell">
                  {field === "userType" && (
                    <>
                      <label>User Type</label>
                      <DropDownComponent
                        inputId="userType-filter"
                        placeHolder="All"
                        pageName="userMaster"
                        dpDownName="status"
                        stateData={userTypedrp}
                        controlName="userType"
                        handleChange={handleUserFilterChange}
                        value={userType}
                        setState={() => { }}
                        multiSelect={false}
                        hideSelectedOptions={false}
                        menuPortalTarget={menuPortalTarget}
                        customStyles={filterSelectStyles}
                      />
                    </>
                  )}

                  {field === "companyName" && (
                    <>
                      <label>{companyLabel}</label>
                      <DropDownComponent
                        inputId="companyName-filter"
                        placeHolder={`Choose ${companyLabel.toLowerCase()}`}
                        pageName="userMaster"
                        dpDownName={companyDropdownConfig.dpDownName}
                        stateData={companyDropdownConfig.stateData}
                        controlName="companyName"
                        handleChange={handleCompanyFilterChange}
                        value={companyName}
                        setState={() => { }}
                        multiSelect={false}
                        hideSelectedOptions={false}
                        menuPortalTarget={menuPortalTarget}
                        customStyles={filterSelectStyles}
                      />
                    </>
                  )}

                  {field === "userRole" && (
                    <>
                      <label>User Role</label>
                      <DropDownComponent
                        inputId="userRole-filter"
                        placeHolder="Choose user role"
                        pageName="userMaster"
                        dpDownName="role"
                        stateData={userRoleData}
                        controlName="roleId"
                        handleChange={handleRoleFilterChange}
                        value={UserRole}
                        setState={() => { }}
                        multiSelect={false}
                        hideSelectedOptions={false}
                        menuPortalTarget={menuPortalTarget}
                        customStyles={filterSelectStyles}
                      />
                    </>
                  )}

                  {field === "status" && (
                    <>
                      <label>Status</label>
                      <DropDownComponent
                        inputId="status-filter"
                        placeHolder="All"
                        pageName="userMaster"
                        dpDownName="status"
                        stateData={statusdrp}
                        controlName="status"
                        handleChange={handleStatusFilterChange}
                        value={status}
                        setState={() => { }}
                        multiSelect={false}
                        hideSelectedOptions={false}
                        menuPortalTarget={menuPortalTarget}
                        customStyles={filterSelectStyles}
                      />
                    </>
                  )}
                </div>

                {activeFields.length > 1 && (
                  <button
                    className="btn filter-row-delete"
                    type="button"
                    onClick={() => removeFilterRow(index)}
                    title="Remove filter"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="quote-filter-footer">
          <button
            className="btn add-filter-row-btn"
            type="button"
            onClick={addFilterRow}
            disabled={activeFields.length >= availableFilterFields.length}
          >
            <i className="fas fa-plus mr-2"></i>
            Add Filter
          </button>
          <div className="filter-action-group">
            <button className="btn reset-btn" type="button" onClick={handleClearAll}>
              Reset
            </button>
            <button
              className="btn apply-btn"
              type="button"
              onClick={() => {
                ApplyFilter();
                onCloseFilter?.();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserMasterFilter;